import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Alert, Tooltip, message } from 'antd';
import {
  RightOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  CheckCircleFilled,
  SyncOutlined,
  ExclamationCircleFilled,
  HomeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import {
  getDashboardStats,
  getRecentActivities,
  getReservations,
  ActivityLog,
  Reservation
} from '../services/api';
import { LimsNavIcon } from '../components/LimsNavIcon';
import './MemberManagement.css';
import './Dashboard.css';

/** 最近动态模拟数据（演示模式 / 开发环境接口为空时预览） */
const MOCK_RECENT_ACTIVITIES: ActivityLog[] = [
  {
    id: 'mock-1',
    type: 'member',
    action: 'join',
    message: '李薇 已完成入组，角色：实验助理',
    time: '今天 10:42',
    actor: { name: '管理员', username: 'admin' },
    priority: 'low'
  },
  {
    id: 'mock-2',
    type: 'instrument',
    action: 'booking',
    message: '共聚焦显微镜预约已通过 · 明天 14:00–17:00',
    time: '今天 09:15',
    actor: { name: '陈思', username: 'chensi' },
    priority: 'low'
  },
  {
    id: 'mock-3',
    type: 'lab-activity',
    action: 'duty',
    message: '本周值日表已发布，请及时查看本月排班',
    time: '今天 08:30',
    actor: { name: '值日组长', username: 'duty' },
    priority: 'medium'
  },
  {
    id: 'mock-4',
    type: 'reagent',
    action: 'stock',
    message: '试剂「DAPI 染液」库存低于安全线，建议补货',
    time: '昨天 17:08',
    actor: { name: '库房', username: 'stock' },
    priority: 'high'
  },
  {
    id: 'mock-5',
    type: 'consumable',
    action: 'order',
    message: '耗材申领单 #2847 已送达 · 15 盒离心管',
    time: '昨天 15:20',
    actor: { name: '物流', username: 'logistics' },
    priority: 'low'
  },
  {
    id: 'mock-6',
    type: 'instrument',
    action: 'maintenance',
    message: '流式细胞仪保养完成，状态已恢复为「可用」',
    time: '昨天 11:40',
    actor: { name: '工程师', username: 'tech' },
    priority: 'medium'
  },
  {
    id: 'mock-7',
    type: 'member',
    action: 'permission',
    message: '王浩 的文献库下载权限已由 PI 更新',
    time: '昨天 09:05',
    actor: { name: '张文华', username: 'pi' },
    priority: 'low'
  },
  {
    id: 'mock-8',
    type: 'lab-activity',
    action: 'meeting',
    message: '课题组例会改至周五 15:00 · 会议室 B',
    time: '前天 19:30',
    actor: { name: '秘书', username: 'secretary' },
    priority: 'medium'
  },
  {
    id: 'mock-9',
    type: 'consumable',
    action: 'alert',
    message: '细胞培养室垃圾桶未及时清理 · 已提醒责任人',
    time: '前天 14:22',
    actor: { name: '安全员', username: 'safety' },
    priority: 'high'
  },
  {
    id: 'mock-10',
    type: 'instrument',
    action: 'queue',
    message: '测序仪排队：当前 2 单待上机，预计今晚完成',
    time: '4月7日 16:00',
    actor: { name: '平台', username: 'platform' },
    priority: 'medium'
  }
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoBrowseOnly, teamDisplayName } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    totalMembers: 0,
    activeInstruments: 0
  });
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<ActivityLog[]>([]);
  const [usingInstrumentsLoading, setUsingInstrumentsLoading] = useState(true);
  const [usingInstruments, setUsingInstruments] = useState<Reservation[]>([]);

  const dateLabel = useMemo(() => dayjs().format('YYYY年M月D日'), []);

  useEffect(() => {
    if (isDemoBrowseOnly) {
      setStatsData({ totalMembers: 42, activeInstruments: 18 });
      setRecentActivities(MOCK_RECENT_ACTIVITIES);
      setLoading(false);
      setActivitiesLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getDashboardStats();
        if (response.success && response.data) {
          setStatsData(response.data);
        }
      } catch (e) {
        console.error('获取统计数据失败:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isDemoBrowseOnly]);

  useEffect(() => {
    if (isDemoBrowseOnly) {
      setUsingInstruments([
        {
          _id: 'demo-1',
          instrumentId: { _id: 'demo-ins-1', name: '共聚焦显微镜', location: 'A-203', responsiblePerson: '王若浩', status: '使用中' },
          userId: { _id: 'demo-user-1', name: '陈思', username: 'chensi', email: 'demo@example.com' },
          startTime: dayjs().subtract(1, 'hour').toISOString(),
          endTime: dayjs().add(2, 'hour').toISOString(),
          actualStartTime: dayjs().subtract(1, 'hour').toISOString(),
          status: '使用中',
          purpose: '样品成像观察'
        },
        {
          _id: 'demo-2',
          instrumentId: { _id: 'demo-ins-2', name: '流式细胞仪', location: 'B-108', responsiblePerson: '高杰', status: '使用中' },
          userId: { _id: 'demo-user-2', name: '王浩', username: 'wanghao', email: 'demo@example.com' },
          startTime: dayjs().subtract(30, 'minute').toISOString(),
          endTime: dayjs().add(90, 'minute').toISOString(),
          actualStartTime: dayjs().subtract(30, 'minute').toISOString(),
          status: '使用中',
          purpose: '细胞分选实验'
        }
      ]);
      setUsingInstrumentsLoading(false);
      return;
    }

    const fetchUsingInstruments = async () => {
      try {
        setUsingInstrumentsLoading(true);
        const response = await getReservations();
        if (response.success && response.data) {
          const usingReservations = response.data
            .filter((r) => r.status === '使用中')
            .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
          setUsingInstruments(usingReservations);
        } else {
          setUsingInstruments([]);
        }
      } catch (e) {
        console.error('获取使用中仪器失败:', e);
        setUsingInstruments([]);
      } finally {
        setUsingInstrumentsLoading(false);
      }
    };

    fetchUsingInstruments();
  }, [isDemoBrowseOnly]);

  useEffect(() => {
    if (isDemoBrowseOnly) {
      return;
    }

    const fetchActivities = async () => {
      try {
        setActivitiesLoading(true);
        const response = await getRecentActivities(10);
        if (response.success && response.data?.length) {
          setRecentActivities(response.data);
        } else if (
          process.env.NODE_ENV === 'development' &&
          (!response.success || !response.data?.length)
        ) {
          // 本地开发接口为空或失败时，用模拟数据方便看时间轴效果
          setRecentActivities(MOCK_RECENT_ACTIVITIES);
        } else {
          setRecentActivities(response.success && response.data ? response.data : []);
        }
      } catch (e) {
        console.error('获取最近活动失败:', e);
        if (process.env.NODE_ENV === 'development') {
          setRecentActivities(MOCK_RECENT_ACTIVITIES);
        }
      } finally {
        setActivitiesLoading(false);
      }
    };

    fetchActivities();
  }, [isDemoBrowseOnly]);

  const pendingDisplay = isDemoBrowseOnly ? 7 : 0;
  const pendingHint = isDemoBrowseOnly ? '3 条关键流程待处理' : '可在审批中心查看';
  const membersFootText = isDemoBrowseOnly ? '8 人当前在岗' : '协作与在岗人数';
  const instrumentsFootText = isDemoBrowseOnly ? '本周可用率 94%' : '可用与使用中设备';
  const pendingIsAlert = pendingDisplay > 0 || isDemoBrowseOnly;

  const go = (path: string) => {
    if (isDemoBrowseOnly) {
      message.info('演示模式下可浏览各模块界面，写入操作需在加入或创建实验室团队后使用。');
    }
    navigate(path);
  };

  const timelineIcon = (item: ActivityLog) => {
    if (item.priority === 'high') {
      return (
        <span className="db-tl-icon db-tl-icon--alert">
          <ExclamationCircleFilled />
        </span>
      );
    }
    if (item.priority === 'medium') {
      return (
        <span className="db-tl-icon db-tl-icon--info">
          <SyncOutlined spin={false} />
        </span>
      );
    }
    return (
      <span className="db-tl-icon db-tl-icon--ok">
        <CheckCircleFilled />
      </span>
    );
  };

  const getUsageDisplay = (reservation: Reservation) => {
    const start = reservation.actualStartTime || reservation.startTime;
    const end = reservation.actualEndTime || reservation.endTime;
    return `${dayjs(start).format('MM-DD HH:mm')} - ${dayjs(end).format('MM-DD HH:mm')}`;
  };

  const quickActions = [
    { title: '实验记录本', path: '/workbench/experiment-records' },
    { title: '仪器预约', path: '/instruments' },
    { title: '值日安排', path: '/lab-activities/duty-schedule' }
  ];

  return (
    <div className="page-container full-height members-lims dashboard-lims">
      <nav className="db-crumb" aria-label="breadcrumb">
        <HomeOutlined className="db-crumb-icon" />
        <span>首页</span>
        <span className="db-crumb-sep">/</span>
        <span>仪表盘</span>
      </nav>

      {isDemoBrowseOnly && (
        <Alert
          type="info"
          showIcon
          className="db-demo-banner"
          message="演示浏览模式"
          description="当前为示例数据。加入或创建团队后可使用完整功能。"
        />
      )}

      <header className="db-header">
        <div>
          <h1 className="db-title">实验室概览</h1>
          <p className="db-subtitle">
            {teamDisplayName || '实验室团队'} · {dateLabel}
          </p>
        </div>
      </header>

      <Spin spinning={loading}>
        <section className="db-metrics" aria-label="核心指标">
          <article className="mm-stat">
            <span className="mm-stat-label">总成员数</span>
            <span className="mm-stat-value">{statsData.totalMembers}</span>
            <div className="mm-stat-foot mm-stat-foot--ok">
              <LimsNavIcon name="fiber_manual_record" sizePx={18} aria-hidden />
              <span>{membersFootText}</span>
            </div>
            <LimsNavIcon name="groups" sizePx={120} className="mm-stat-deco" aria-hidden />
          </article>

          <article className="mm-stat">
            <span className="mm-stat-label">活跃设备</span>
            <span className="mm-stat-value">{statsData.activeInstruments}</span>
            <div className="mm-stat-foot mm-stat-foot--neutral">
              <LimsNavIcon name="show_chart" sizePx={18} aria-hidden />
              <span>{instrumentsFootText}</span>
            </div>
            <LimsNavIcon name="biotech" sizePx={120} className="mm-stat-deco" aria-hidden />
          </article>

          <article className="mm-stat">
            <span className="mm-stat-label">待处理事项</span>
            <span
              className={`mm-stat-value${pendingIsAlert ? ' mm-stat-value--alert' : ''}`}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {String(pendingDisplay).padStart(2, '0')}
            </span>
            <div className={`mm-stat-foot ${pendingIsAlert ? 'mm-stat-foot--alert' : 'mm-stat-foot--neutral'}`}>
              <LimsNavIcon name={pendingIsAlert ? 'error' : 'info'} sizePx={18} aria-hidden />
              <span>{pendingHint}</span>
            </div>
            <LimsNavIcon name="pending_actions" sizePx={120} className="mm-stat-deco" aria-hidden />
          </article>

          <article className="db-quick-panel db-gradient-hero" aria-label="快捷入口">
            <div className="db-quick-panel-head">
              <ThunderboltOutlined className="db-quick-bolt" />
              <span className="db-quick-title">快捷入口</span>
            </div>
            <ul className="db-quick-list">
              {quickActions.map((q) => (
                <li key={q.path}>
                  <button type="button" className="db-quick-link" onClick={() => go(q.path)}>
                    <span>{q.title}</span>
                    <RightOutlined className="db-quick-arrow" />
                  </button>
                </li>
              ))}
            </ul>
          </article>
        </section>
      </Spin>

      <div className="db-below-metrics">
        <section className="db-panel db-surface-low db-timeline-panel" aria-label="最近动态">
          <h2 className="db-panel-title db-panel-title--solo">最近动态</h2>
          <Spin spinning={activitiesLoading}>
            <ul className="db-timeline">
              {recentActivities.length === 0 && !activitiesLoading ? (
                <li className="db-timeline-empty">暂无活动记录</li>
              ) : (
                recentActivities.map((item) => (
                  <li key={item.id} className="db-timeline-item">
                    {timelineIcon(item)}
                    <div className="db-timeline-body">
                      <p className="db-timeline-msg">{item.message}</p>
                      <time className="db-timeline-time">{item.time}</time>
                    </div>
                  </li>
                ))
              )}
            </ul>
            <div className="db-trend-pill db-surface-lowest">
              <span className="db-trend-label">本周协作</span>
              <span className="db-trend-value">
                {isDemoBrowseOnly ? '28 次互动 · 12 条新记录（示例）' : '活跃度稳定'}
              </span>
            </div>
          </Spin>
        </section>

        <section className="db-panel db-surface-low db-using-panel" aria-label="仪器使用状态">
          <h2 className="db-panel-title db-panel-title--solo">仪器使用状态</h2>
          <Spin spinning={usingInstrumentsLoading}>
            {usingInstruments.length === 0 && !usingInstrumentsLoading ? (
              <div className="db-using-empty">当前暂无使用中的仪器</div>
            ) : (
              <ul className="db-using-list">
                {usingInstruments.map((item) => (
                  <li key={item._id || item.startTime} className="db-using-item">
                    <div className="db-using-main">
                      <span className="db-using-name">
                        {typeof item.instrumentId === 'object' && item.instrumentId
                          ? item.instrumentId.name
                          : '未知仪器'}
                      </span>
                      <span className="db-using-meta">
                        位置：
                        {typeof item.instrumentId === 'object' && item.instrumentId
                          ? item.instrumentId.location || '—'
                          : '—'}{' '}
                        · 负责人：
                        {typeof item.instrumentId === 'object' && item.instrumentId
                          ? item.instrumentId.responsiblePerson || '—'
                          : '—'}
                      </span>
                      <span className="db-using-meta">
                        使用人：
                        {typeof item.userId === 'object' && item.userId
                          ? item.userId.name || item.userId.username || '—'
                          : '—'}{' '}
                        · 使用时段：{getUsageDisplay(item)}
                      </span>
                    </div>
                    <span className="db-using-pill">使用中</span>
                  </li>
                ))}
              </ul>
            )}
          </Spin>
        </section>
      </div>

      <Tooltip title={isDemoBrowseOnly ? '演示模式' : '快速发起'}>
        <button
          type="button"
          className="db-fab"
          aria-label="快速发起"
          onClick={() => go('/workbench/application-submission')}
        >
          <PlusOutlined />
        </button>
      </Tooltip>
    </div>
  );
};

export default Dashboard;
